/**
 * Brand kits — api-service wire contract (snake_case), round 6 §3.1.
 *
 * A tenant's logos and colour palettes, set up once in Settings and chosen per distribution in
 * step 3 (`RecipientBrandApi.kit_id`, `logo_id`, `palette_id`). Same access rule as the template
 * routes: 404 unless the tenant is on the new Story path with distribution on.
 *
 * Base path: /api/v2/distribution-brand-kits
 *
 *   GET    ""                        → BrandKitListApi (live kits, the default first, then by name)
 *   POST   ""                        BrandKitContentApi → 202 BrandKitAcceptedApi
 *   GET    "/edits/{request_id}"     → EditStatusApi
 *   POST   "/logos"                  multipart/form-data, one `file` field → 201 LogoUploadedApi
 *   GET    "/{kit_id}"               → BrandKitApi (404 if archived or another tenant's)
 *   PUT    "/{kit_id}"               BrandKitContentApi → 202 BrandKitAcceptedApi (replaces)
 *   DELETE "/{kit_id}"               → 202 BrandKitAcceptedApi (archives)
 *   POST   "/{kit_id}/default"       → 202 BrandKitAcceptedApi
 *
 * Errors: 409 `BRAND_KIT_NAME_TAKEN`; a saved kit with a bad logo key is 422 with one row error per
 * logo (`position` = the 1-based logo, `field: "logos.object_key"`); uploads answer 413
 * `logo_too_large`, 422 `logo_not_image` or 503 `BRAND_ASSETS_NOT_CONFIGURED`. Messages are shown
 * verbatim (getApiErrorMessage).
 */
import type { UserRefApi } from "./distributions.types";

export type BrandLogoVariantApi = "light" | "dark";

/** An SVG is turned into a PNG in the browser before it is uploaded. */
export type BrandLogoContentTypeApi = "image/png" | "image/jpeg";

/** magic-core `BrandLogo`: what a kit's content stores for one logo. */
export interface BrandLogoInputApi {
  id: string;
  /** 1–120 characters. */
  name: string;
  /** `brand-kits/{tenant_id}/logos/{logo_id}.{png|jpg}`, from the upload. */
  object_key: string;
  /** `light`: drawn on light backgrounds; `dark`: on dark ones. */
  variant: BrandLogoVariantApi;
  content_type: BrandLogoContentTypeApi;
  width: number | null;
  height: number | null;
}

/** A logo as read: the stored fields plus a 15-minute signed GET, or null when it can't be signed. */
export interface BrandLogoApi extends BrandLogoInputApi {
  url: string | null;
}

export interface BrandPaletteApi {
  id: string;
  /** 1–120 characters. */
  name: string;
  /** "#RRGGBB", 1–12 of them, in order: the first is the accent; charts use them in turn. */
  colors: string[];
}

/** magic-core `BrandKitContent`: the body of a create or a replace. Unknown keys are refused. */
export interface BrandKitContentApi {
  /** 1–120 characters, unique among the tenant's live kits (ignoring case). */
  name: string;
  /** The organisation's name as copies show it; null → the tenant's display name. */
  display_name: string | null;
  /** One or two letters shown when there is no logo. */
  mark: string | null;
  logos: BrandLogoInputApi[];
  palettes: BrandPaletteApi[];
}

export interface BrandKitApi {
  id: string;
  name: string;
  display_name: string | null;
  mark: string | null;
  /** The kit a new distribution starts with; one per tenant at most. */
  is_default: boolean;
  logos: BrandLogoApi[];
  palettes: BrandPaletteApi[];
  created_by: UserRefApi | null;
  updated_at: string;
}

export interface BrandKitListApi {
  items: BrandKitApi[];
}

export interface BrandKitAcceptedApi {
  kit_id: string;
  request_id: string;
}

/** 201 from `POST /logos`: the stored file, ready to add to a kit's `logos`. */
export interface LogoUploadedApi {
  /** Becomes `BrandLogoInputApi.id`. */
  logo_id: string;
  object_key: string;
  content_type: BrandLogoContentTypeApi;
  width: number | null;
  height: number | null;
  /** A 15-minute signed GET to show it right away; null when it can't be signed. */
  url: string | null;
}

/** magic-core's bounds (`schemas/brand/kit.py`) and the upload size (§2). */
export const BRAND_KIT_LIMITS = {
  maxLogos: 10,
  maxPalettes: 10,
  maxColors: 12,
  nameMax: 120,
  displayNameMax: 120,
  markMax: 2,
  maxLogoBytes: 2_097_152,
} as const;

/**
 * Error codes the brand kit routes answer with. A save's name conflict comes early (409 on the
 * POST/PUT) or from the edit poll (`status: "failed"`, same code); the poll can also fail with
 * `BRAND_KIT_LOGO_OUTSIDE_TENANT` or `BRAND_KIT_NOT_FOUND`.
 */
export const BRAND_KIT_ERROR_CODES = {
  nameTaken: "BRAND_KIT_NAME_TAKEN",
  logoOutsideTenant: "BRAND_KIT_LOGO_OUTSIDE_TENANT",
  kitNotFound: "BRAND_KIT_NOT_FOUND",
  logoTooLarge: "logo_too_large",
  logoNotImage: "logo_not_image",
} as const;

/**
 * Logo uploads aren't set up on this server (no bucket). api-service labels every 5xx
 * `internal_error`, so the state is keyed on the status, not a code.
 */
export const BRAND_ASSETS_UNAVAILABLE_STATUS = 503;
